import { FileText, Download, Play } from 'lucide-react'

interface Attachment {
  id: number
  signed_id: string
  filename: string
  content_type: string
  byte_size: number
  url: string
  is_video?: boolean
  is_image?: boolean
}

interface MessageAttachmentsProps {
  attachments: Attachment[]
  onImageClick?: (images: { url: string; filename: string }[], index: number) => void
  onVideoClick?: (url: string, filename: string) => void
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

function isImage(attachment: Attachment): boolean {
  if (attachment.is_image !== undefined) return attachment.is_image
  return attachment.content_type.startsWith('image/')
}

function isVideo(attachment: Attachment): boolean {
  if (attachment.is_video !== undefined) return attachment.is_video
  return attachment.content_type.startsWith('video/')
}

function isAudio(attachment: Attachment): boolean {
  return attachment.content_type.startsWith('audio/')
}

export default function MessageAttachments({
  attachments,
  onImageClick,
  onVideoClick,
}: MessageAttachmentsProps) {
  if (!attachments || attachments.length === 0) {
    return null
  }

  const imageAttachments = attachments.filter((a) => isImage(a))
  const videoAttachments = attachments.filter((a) => isVideo(a))
  const audioAttachments = attachments.filter((a) => isAudio(a))
  const fileAttachments = attachments.filter((a) => !isImage(a) && !isVideo(a) && !isAudio(a))

  const handleImageClick = (index: number) => {
    if (onImageClick) {
      const images = imageAttachments.map((a) => ({
        url: a.url,
        filename: a.filename,
      }))
      onImageClick(images, index)
    }
  }

  const handleVideoClick = (attachment: Attachment) => {
    if (onVideoClick) {
      onVideoClick(attachment.url, attachment.filename)
    }
  }

  return (
    <div className="mt-2 space-y-2">
      {/* Image attachments */}
      {imageAttachments.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {imageAttachments.map((attachment, index) => (
            <div
              key={attachment.id}
              className="relative cursor-pointer group"
              onClick={() => handleImageClick(index)}
            >
              <img
                src={attachment.url}
                alt={attachment.filename}
                className="max-w-[200px] max-h-[200px] rounded-lg object-cover border border-gray-200 hover:border-blue-400 transition-colors"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 rounded-lg transition-colors" />
            </div>
          ))}
        </div>
      )}

      {/* Video attachments */}
      {videoAttachments.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {videoAttachments.map((attachment) => (
            <div
              key={attachment.id}
              className="relative cursor-pointer group"
              onClick={() => handleVideoClick(attachment)}
            >
              {/* Video thumbnail from first frame */}
              <video
                src={attachment.url}
                preload="metadata"
                muted
                playsInline
                className="max-w-[200px] max-h-[200px] rounded-lg object-cover border border-gray-200 hover:border-blue-400 transition-colors"
                onLoadedMetadata={(e) => {
                  const video = e.target as HTMLVideoElement
                  video.currentTime = 0.1
                }}
              />

              {/* Play button overlay */}
              <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors rounded-lg">
                <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                  <Play className="w-7 h-7 text-gray-800 ml-1" fill="currentColor" />
                </div>
              </div>

              {/* Video badge */}
              <div className="absolute top-2 left-2 px-2 py-0.5 rounded bg-black/60 text-white text-[10px] font-bold">
                VIDEO
              </div>

              {/* File size badge */}
              <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-black/60 text-white text-[10px]">
                {formatFileSize(attachment.byte_size)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Audio attachments */}
      {audioAttachments.length > 0 && (
        <div className="flex flex-col gap-1">
          {audioAttachments.map((attachment) => (
            <div
              key={attachment.id}
              className="w-64"
            >
              <audio
                controls
                preload="metadata"
                className="w-full"
                src={attachment.url}
              />
            </div>
          ))}
        </div>
      )}

      {/* File attachments */}
      {fileAttachments.length > 0 && (
        <div className="flex flex-col gap-1">
          {fileAttachments.map((attachment) => (
            <a
              key={attachment.id}
              href={attachment.url}
              download={attachment.filename}
              className="flex items-center gap-2 p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors text-sm max-w-xs"
            >
              <FileText className="w-5 h-5 text-gray-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="truncate text-gray-700">{attachment.filename}</div>
                <div className="text-xs text-gray-500">{formatFileSize(attachment.byte_size)}</div>
              </div>
              <Download className="w-4 h-4 text-gray-400 flex-shrink-0" />
            </a>
          ))}
        </div>
      )}
    </div>
  )
}
